import React from 'react';
import styled from 'styled-components';

/*
 * GowriRow — pipe-separated horizontal slot list (8 slots).
 *
 *  - Desktop: 8 slots in CSS Grid `repeat(8, 1fr)`, pipes via ::before
 *  - Mobile (<768px): 1 slot per row, no pipes; nature kept via icon + color
 *  - Auspicious: green text + ⊕ icon
 *  - Inauspicious: red text + ⊖ icon
 *  - Neutral: primary text, no icon
 *  - Active slot: font-weight bold (NOT color — color already conveys nature)
 */

const NATURE_GLYPHS = {
  auspicious:   '⊕', // ⊕
  inauspicious: '⊖', // ⊖
  neutral:      '',
};

const Row = styled.ul`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  list-style: none;
  margin: 0;
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[3]}px;
  gap: 0;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    grid-template-columns: 1fr;
    gap: ${p => p.theme.spacing[1]}px;
  }
`;

const SlotItem = styled.li`
  position: relative;
  padding: 4px ${p => p.theme.spacing[2]}px;
  text-align: center;
  font-size: 13px;
  color: ${p => {
    const toneKey = p.nature === 'auspicious' ? 'green' : p.nature === 'inauspicious' ? 'red' : 'neutral';
    const t = p.theme.colors.tone[toneKey];
    return (!t || t.text === 'inherit') ? p.theme.colors.text.primary : t.text;
  }};
  font-weight: ${p => p.active ? p.theme.typography.weights.bold : p.theme.typography.weights.regular};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};

  &:not(:first-child)::before {
    content: '│';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    color: ${p => p.theme.colors.border};
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    text-align: left;
    padding: 4px 0;
    border-bottom: 1px solid ${p => p.theme.colors.border};
    &:last-child { border-bottom: none; }

    &:not(:first-child)::before {
      content: none;
    }
  }

  @media print { color: #000; }
`;

const Glyph = styled.span`
  margin-right: 4px;
  font-size: 12px;
`;

const TimeText = styled.span`
  margin-left: 6px;
  font-family: ${p => p.theme.typography.monoFamily};
  font-size: 12px;
  color: ${p => p.theme.colors.time?.mono || p.theme.colors.text.secondary};
  @media print { color: #000; }
`;

const GowriRow = ({ slots = [], activeSlotIndex = null, lang = 'en' }) => {
  if (!slots || slots.length === 0) return null;

  return (
    <Row role="list">
      {slots.map((slot, i) => {
        const nature = slot.nature || 'neutral';
        const isActive = i === activeSlotIndex;
        const name = lang === 'ta' ? (slot.name_ta || slot.name) : slot.name;
        const glyph = NATURE_GLYPHS[nature];
        return (
          <SlotItem
            key={`${slot.name}-${i}`}
            role="listitem"
            nature={nature}
            active={isActive}
            aria-label={`${slot.name}, ${slot.start}, ${nature}`}
          >
            {glyph && <Glyph aria-hidden="true">{glyph}</Glyph>}
            {name}
            <TimeText>{slot.start}</TimeText>
          </SlotItem>
        );
      })}
    </Row>
  );
};

export default GowriRow;
